/**
 * Feature 104: app-level ownership of the Care Concierge contact dialog.
 *
 * Post-Kirkland revamp (2026-08-24 call): in-app entry points open the
 * contact form directly — no chooser dialog. The paid 60-minute consultation
 * is reachable only from the dashboard card (`openConsultation`) and the
 * public /care-concierge page, which renders ConciergeChooserOptions inline.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { CalendlyModal } from '@/shared/components/CalendlyModal';
import { trackRumAction } from '@/shared/observability/rum';
import { CareConciergeForm } from '@/features/dashboard/care-seeker/components/CareConciergeForm';
import type { PageOrigin } from '@/features/provider-directory/components/CareConciergContactForm/types';
import {
  CONCIERGE_CALENDLY_MODAL_COPY,
  CONCIERGE_CALENDLY_URLS,
  type ConsultationTier,
} from '../conciergeCalendly';

type ConciergeChooserContextValue = {
  /** Opens the contact form dialog (the only in-app contact path). */
  openConcierge: (source: PageOrigin) => void;
  /** Opens the paid 60-minute consultation Calendly directly (dashboard card). */
  openConsultation: (source: PageOrigin) => void;
};

// Default no-op value: the provider mounts once at app level; components
// rendered without it (isolated tests) degrade to inert entry points instead
// of crashing their whole page.
const ConciergeChooserContext = createContext<ConciergeChooserContextValue>({
  openConcierge: () => {},
  openConsultation: () => {},
});

export const useConciergeChooser = (): ConciergeChooserContextValue =>
  useContext(ConciergeChooserContext);

/**
 * Opens the given consultation tier's Calendly and reports the funnel event.
 * Used by the public page's inline chooser.
 */
export const useConciergeCalendly = (source: PageOrigin) => {
  const [tier, setTier] = useState<ConsultationTier | null>(null);

  const openCalendly = useCallback(
    (nextTier: ConsultationTier) => {
      trackRumAction('concierge_calendly_opened', { source, tier: nextTier });
      setTier(nextTier);
    },
    [source],
  );

  return { tier, openCalendly, closeCalendly: () => setTier(null) };
};

export const ConciergeCalendlyHost = ({
  tier,
  onClose,
}: {
  tier: ConsultationTier | null;
  onClose: () => void;
}) => (
  <CalendlyModal
    open={tier !== null}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
    url={tier ? CONCIERGE_CALENDLY_URLS[tier] : ''}
    title={tier ? CONCIERGE_CALENDLY_MODAL_COPY[tier].title : ''}
    description={tier ? CONCIERGE_CALENDLY_MODAL_COPY[tier].description : ''}
  />
);

export function ConciergeChooserProvider({ children }: { children: ReactNode }) {
  const [formSource, setFormSource] = useState<PageOrigin | null>(null);
  const [consultationTier, setConsultationTier] = useState<ConsultationTier | null>(null);

  const openConcierge = useCallback((source: PageOrigin) => {
    trackRumAction('concierge_form_opened', { source });
    setFormSource(source);
  }, []);

  const openConsultation = useCallback((source: PageOrigin) => {
    trackRumAction('concierge_calendly_opened', { source, tier: '60min' });
    setConsultationTier('60min');
  }, []);

  const close = () => setFormSource(null);

  const value = useMemo(
    () => ({ openConcierge, openConsultation }),
    [openConcierge, openConsultation],
  );

  return (
    <ConciergeChooserContext.Provider value={value}>
      {children}

      <Dialog open={formSource !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent className="w-full bg-background p-4 sm:max-w-4xl sm:p-6 max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Contact Care Concierge</DialogTitle>
            <DialogDescription>
              Send us your details and our Care Concierge team will reach out
            </DialogDescription>
          </DialogHeader>
          {formSource !== null && (
            <CareConciergeForm pageOrigin={formSource} onSuccess={close} onClose={close} />
          )}
        </DialogContent>
      </Dialog>

      <ConciergeCalendlyHost tier={consultationTier} onClose={() => setConsultationTier(null)} />
    </ConciergeChooserContext.Provider>
  );
}
